import * as yup from 'yup';
import { contactSchema, contactSchemaSoft } from '../../validationSchemas/contactValidation';
import '../../validationSchemas/yupExtensions';
import {
    serialNumMatchRegEx, extAlphaNumMultiLineMatchRegex, nullableDate, websiteUrlSchema,
} from '../../validationSchemas/common';
import { InstrumentAndRequestStep, OrganisationAndContact, YesNo } from '../../api/web-api-client';
import { Validation } from '../../components/forms/FormikForm/types';
import { formatDateStringToUTC } from '../../utils';

// eslint-disable-next-line import/prefer-default-export
export const organisationAndContactSubmitValidation = yup.object<Validation<OrganisationAndContact>>({
    businessWebsiteAddress: websiteUrlSchema('Business website address is not a valid website address')
        .label('Business website address')
        .maxLength(100)
        .optional(),
    contact: yup.mixed().when('isPrincipalContact', {
        is: (x: YesNo | undefined) => x === YesNo.No,
        then: () => contactSchema(),
        otherwise: () => yup.mixed(),
    }),
});

export const organisationAndContactSaveValidation = yup.object<Validation<OrganisationAndContact>>({
    businessWebsiteAddress: websiteUrlSchema('Business website address is not a valid website address')
        .label('Business website address')
        .nullable()
        .maxLength(100),
    contact: yup.mixed().when('isPrincipalContact', {
        is: (x: YesNo | undefined) => x === YesNo.No,
        then: () => contactSchemaSoft(),
        otherwise: () => yup.mixed(),
    }),
});

export const instrumentAndRequestSubmitValidation = yup.object<Validation<InstrumentAndRequestStep>>({
    hasSerialNumber: yup.mixed<keyof typeof YesNo>()
        .label('Does this instrument/artefact have a serial number available?')
        .required()
        .oneOf(Object.values(YesNo), 'Does this instrument/artefact have a serial number available? is required'),
    serialNumber: yup.string().when('hasSerialNumber', {
        is: (x: YesNo | undefined) => x === YesNo.Yes,
        then: () => yup.string()
            .label('Serial number')
            .required()
            .minEntered(1)
            .maxLength(100)
            // To do - Explore create new Schema or stringExtensions Methods to support this
            .matches(
                serialNumMatchRegEx,
                'Serial number has invalid characters. Please use only letters, periods, numbers, and keyboard characters.',
            ),
    }),
    manufacturer: yup.string()
        .label('Manufacturer')
        .required()
        .minEntered(2)
        .maxLength(100),
    model: yup.string()
        .label('Model')
        .required()
        .minEntered(2)
        .maxLength(100),
    description: yup.string()
        .label('Description')
        .optional()
        .minEntered(2)
        .maxLength(400)
        // To do - Explore create new Schema or stringExtensions Methods to support this
        .test(
            'description-valid-characters',
            'Description has invalid characters. Please remove any invalid characters such as degrees (°), semicolon (;), backslash (\\), or pipe (|) to continue',
            (value) => {
                if (!value || value.trim() === '') {
                    return true; // Skip validation if the field is blank or null
                }
                return extAlphaNumMultiLineMatchRegex.test(value); // Apply regex validation if not blank
            },
        ),
    numberOfItems: yup.number()
        .label('Number of items')
        .typeError('Number of items is a required field')
        .required()
        .min(1, 'Number of items cannot be less than 1')
        .max(100, 'Number of items cannot be greater than 100'),
    measurementCategory: yup.string()
        .label('Measurement category')
        .required(),
    instrumentOrArtefactType: yup.string()
        .label('Instrument or artefact type')
        .required(),
    previousQuoteOrReportNumber: yup.string()
        .label('Previous NMI Quote or Report number')
        .minEntered(2)
        .maxLength(100),
    testingAndCalibrationRequirements: yup.string()
        .label('Testing and calibration requirements')
        .required()
        .minEntered(2)
        .maxLength(2000)
        // To do - Explore create new Schema or stringExtensions Methods to support this
        .test(
            'testing-calib-valid-characters',
            'Testing and calibration requirements has invalid characters. Please remove any invalid characters such as degrees (°), semicolon (;), backslash (\\), or pipe (|) to continue',
            (value) => {
                if (!value || value.trim() === '') {
                    return true; // Skip validation if the field is blank or null
                }
                return extAlphaNumMultiLineMatchRegex.test(value); // Apply regex validation if not blank
            },
        ),
    measurementReportAndCertificateRequired: yup.string()
        .label('Measurement report and certificate required')
        .required(),
    preferredInstrumentOrArtefactAvailabilityDate: nullableDate('Preferred date must be a valid date format (dd/mm/yyyy)')
        .test(
            'is-future-date',
            'Cannot select a date earlier than today', // Custom error for past dates
            (value) => {
                if (!value) {
                    return true; // Null or undefined is valid
                }

                const now = formatDateStringToUTC(new Date());
                const valToTest = formatDateStringToUTC(new Date(value as string));
                return valToTest && now && valToTest >= now;
            },
        ),
});

export const instrumentAndRequestSaveValidation = yup.object<Validation<InstrumentAndRequestStep>>({
    hasSerialNumber: yup.mixed<keyof typeof YesNo>()
        .label('Does this instrument/artefact have a serial number available?')
        .nullable(),
    serialNumber: yup.string().when('hasSerialNumber', {
        is: (x: YesNo | undefined) => x === YesNo.Yes,
        then: () => yup.string()
            .label('Serial number')
            .nullable()
            .minEntered(1)
            .maxLength(100)
            // To do - Explore create new Schema or stringExtensions Methods to support this
            .matches(
                serialNumMatchRegEx,
                'Serial number has invalid characters. Please use only letters, periods, numbers, and keyboard characters.',
            ),
    }),
    manufacturer: yup.string()
        .label('Manufacturer')
        .nullable()
        .minEntered(2)
        .maxLength(100),
    model: yup.string()
        .label('Model')
        .nullable()
        .minEntered(2)
        .maxLength(100),
    description: yup.string()
        .label('Description')
        .optional()
        .minEntered(2)
        .maxLength(400)
        // To do - Explore create new Schema or stringExtensions Methods to support this
        .test(
            'description-valid-characters',
            'Description has invalid characters. Please use only letters, periods, numbers, and keyboard characters.',
            (value) => {
                if (!value || value.trim() === '') {
                    return true; // Skip validation if the field is blank or null
                }
                return extAlphaNumMultiLineMatchRegex.test(value); // Apply regex validation if not blank
            },
        ),
    numberOfItems: yup.number()
        .label('Number of items')
        .transform(
            (value) => (Number.isNaN(value) ? undefined : value),
        ).nullable()
        .min(1, 'Number of items cannot be less than 1')
        .max(100, 'Number of items cannot be greater than 100'),
    measurementCategory: yup.string()
        .label('Measurement category')
        .nullable(),
    instrumentOrArtefactType: yup.string()
        .label('Instrument or artefact type')
        .nullable(),
    previousQuoteOrReportNumber: yup.string()
        .label('Previous NMI Quote or Report number')
        .nullable()
        .minEntered(2)
        .maxLength(100),
    testingAndCalibrationRequirements: yup.string()
        .label('Testing and calibration requirements')
        .nullable()
        .minEntered(2)
        .maxLength(2000)
        // To do - Explore create new Schema or stringExtensions Methods to support this
        .test(
            'testing-calib-valid-characters',
            'Testing and calibration requirements has invalid characters. Please use only letters, periods, numbers, and keyboard characters.',
            (value) => {
                if (!value || value.trim() === '') {
                    return true; // Skip validation if the field is blank or null
                }
                return extAlphaNumMultiLineMatchRegex.test(value); // Apply regex validation if not blank
            },
        ),
    measurementReportAndCertificateRequired: yup.string()
        .label('Measurement report and certificate required')
        .nullable(),
});
